"use client";

import React, { useState } from "react";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { Clock } from "lucide-react";
import { Spin } from "antd";
import { useUpdateBasicProfile } from "../../../../hooks/api/Tutors/UpdateProfile/useUpdateBasicProfile";

type Props = { timezone: string };

const TimeZoneEditor = (props: Props) => {
  const [timezone, setTimezone] = useState(props.timezone || "");

  const usTimezones = Object.fromEntries(
    Object.entries(allTimezones).filter(
      ([key]) => key.startsWith("US/") || key.includes("America/")
    )
  );

  const { mutate, isLoading } = useUpdateBasicProfile();
  const handleTimezoneChange = (timezone: any) => {
    mutate({
      timezone: timezone?.value,
    });
  };

  // Custom styles for react-select to match Ant Design
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: "6px",
      borderColor: state.isFocused ? "#1677ff" : "#d9d9d9",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(5, 145, 255, 0.1)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#1677ff" : "#4096ff",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 100,
    }),
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-100 mt-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
          <Clock size={20} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Time Zone</h3>
          <p className="text-sm text-gray-500">
            Set your local time zone for scheduling
          </p>
        </div>
      </div>

      <div className="relative">
        <Spin spinning={isLoading}>
          <TimezoneSelect
            value={timezone}
            onChange={(tz: any) => {
              setTimezone(tz?.value || "");
              handleTimezoneChange(tz);
            }}
            timezones={usTimezones}
            placeholder="Select your time zone"
            className="z-[50]"
            styles={customStyles}
          />
        </Spin>
      </div>

      <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
        <span className="text-blue-600 font-medium">Note:</span>
        <p>
          Your availability will be shown to students in their local time,
          automatically converted from your settings.
        </p>
      </div>
    </div>
  );
};

export default TimeZoneEditor;
